import { Injectable } from '@angular/core';
import { SenderTypeE } from '@enums/message.enum';
import { MessagesHelperService } from '@helpers/messages-helper.service';
import { MessageErrorI } from '@interfaces/message-error.interface';
import { DataFailMessageI, MessageI, MessageStatusT } from '@interfaces/message.interface';
import { PageI } from '@interfaces/page.interface';
import { TranscriptionI } from '@interfaces/transcription.interface';
import { ComponentStore } from '@ngrx/component-store';
import { BehaviorSubject, Observable, firstValueFrom, map } from 'rxjs';

export interface MessageStore {
  messages: { [id: string]: PageI<MessageI> };
  scheduled: { [id: string]: PageI<MessageI> };
  scrollPositions: { [id: string]: number };
}

@Injectable({
  providedIn: 'root',
})
export class MessageStoreService extends ComponentStore<MessageStore> {
  public messages$: Observable<{ [id: string]: PageI<MessageI> }> = this.select((state) => state.messages);
  public scheduled$: Observable<{ [id: string]: PageI<MessageI> }> = this.select((state) => state.scheduled);
  public scrollPositions$: Observable<{ [id: string]: number }> = this.select((state) => state.scrollPositions);
  private messageAddedSubjects: { [id: string]: BehaviorSubject<MessageI | undefined> } = {};

  constructor(private readonly messageHelperService: MessagesHelperService) {
    super({
      messages: {},
      scheduled: {},
      scrollPositions: {},
    });
  }

  public getScrollPosition(chatId: string): Observable<number> {
    return this.scrollPositions$.pipe(map((scrollPositions) => scrollPositions[chatId]));
  }

  public setScrollPosition(chatId: string, position: number) {
    this.setState((state) => ({
      ...state,
      scrollPositions: {
        ...state.scrollPositions,
        [chatId]: position,
      },
    }));
  }

  public getMessagesByChatId(chatId: string): Observable<PageI<MessageI>> {
    return this.select((state) => state.messages[chatId]);
  }

  public getMessageById(chatId: string, messageId: string): Observable<MessageI | undefined> {
    return this.select((state) => {
      const messagesPage = state.messages[chatId];
      return messagesPage ? messagesPage.data.find((msg) => msg._id === messageId) : undefined;
    });
  }

  public getScheduledMessages(): Observable<MessageI[]> {
    return this.scheduled$.pipe(
      map((scheduled) =>
        Object.values(scheduled)
          .map((paginator) => paginator.data)
          .flat()
      )
    );
  }

  public getScheduledMessagesByChatId(chatId: string): Observable<MessageI[]> {
    return this.select((state) => state.scheduled[chatId]?.data ?? []);
  }

  public async populateStore(chatId: string, page: PageI<MessageI>) {
    let { data } = page;
    data = this.filterMessagesWrongTemplate(data);

    this.setState((state) => {
      const preserved = this.preserveUnconfirmedMessages(state.messages[chatId]?.data, data);
      return {
        ...state,
        messages: {
          ...state.messages,
          [chatId]: {
            ...page,
            data: preserved.length ? [...data, ...preserved] : data,
          },
        },
      };
    });
  }

  public async populateStorePages(pages: { chat: string; page: PageI<MessageI> }[]) {
    this.setState((state) => {
      const newMessages = { ...(state?.messages ?? {}) };
      for (const { chat, page } of pages) {
        const incoming = this.filterMessagesWrongTemplate(page.data);
        const current = newMessages[chat];

        // OMNI-7778: a socket reconnect refetches page-1 of every chat; replacing the store with it
        // truncated the open chat's scrolled history (e.g. 170 -> 50), re-rendering and jumping the
        // panel. Never shrink a loaded history: merge, appending only messages not yet present (the
        // view re-sorts by createdAt). Nothing new -> keep the exact prior reference so the open
        // chat does not re-render on reconnect. Confirmed pending/fail already live in `current`.
        if (current?.data?.length) {
          const existingIds = new Set(current.data.map((m) => m._id));
          const missing = incoming.filter((m) => m._id && !existingIds.has(m._id));
          if (!missing.length) {
            continue;
          }
          newMessages[chat] = { ...current, data: [...current.data, ...missing] };
          continue;
        }

        const preserved = this.preserveUnconfirmedMessages(current?.data, incoming);
        newMessages[chat] = {
          ...page,
          data: preserved.length ? [...incoming, ...preserved] : incoming,
        };
      }
      return {
        ...state,
        messages: {
          ...newMessages,
        },
      };
    });
  }

  // Keeps locally-created optimistic messages (pending/fail) that the incoming backend
  // batch doesn't include, so a store refresh (e.g. socket reconnect) never drops a message
  // the agent already sent. Dedup by _id/oldId mirrors addMessage.
  private preserveUnconfirmedMessages(previous: MessageI[] | undefined, incoming: MessageI[]): MessageI[] {
    if (!previous?.length) {
      return [];
    }
    const incomingIds = new Set<string>();
    incoming.forEach((m) => {
      if (m._id) incomingIds.add(m._id);
      if (m.oldId) incomingIds.add(m.oldId);
    });
    return previous.filter(
      (m) =>
        (m.status === 'pending' || m.status === 'fail') &&
        !incomingIds.has(m._id!) &&
        !(m.oldId && incomingIds.has(m.oldId))
    );
  }

  public async populateSchedule(chatId: string | 'all', page: PageI<MessageI>) {
    this.setState((state) => ({
      ...state,
      scheduled: {
        ...state.scheduled,
        [chatId]: page,
      },
    }));
  }

  public async clearStoreByChatId(chatId: string) {
    this.setState((state) => {
      const { messages, ...rest } = state;
      const updatedMessages = { ...messages };
      delete updatedMessages[chatId];

      return {
        ...rest,
        messages: updatedMessages,
      };
    });
  }

  public unshiftMessagesByChatId(chatId: string, page: PageI<MessageI>) {
    let { data } = page;

    if (!data.length) {
      return;
    }

    data = this.filterMessagesWrongTemplate(data);

    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [chatId]: {
          data: [...data, ...(state.messages[chatId]?.data ?? [])],
          meta: page.meta,
        },
      },
    }));
  }

  public unshiftMessagesByChatIds(pages: { chat: string; page: PageI<MessageI> }[]) {
    this.setState((state) => {
      const newMessages = state.messages;
      for (const { page, chat } of pages) {
        let { data, meta } = page;
        if (!data.length) {
          continue;
        }
        data = this.filterMessagesWrongTemplate(data);
        newMessages[chat] = {
          data: [...data, ...(newMessages[chat]?.data ?? [])],
          meta,
        };
      }
      return {
        ...state,
        messages: {
          ...newMessages,
        },
      };
    });
  }

  public pushMessagesByChatId(chatId: string, page: PageI<MessageI>) {
    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [chatId]: {
          data: [...state.messages[chatId].data, ...page.data],
          meta: page.meta,
        },
      },
    }));
  }

  public populateOrUnshiftMessagesByChatId(chatId: string, page: PageI<MessageI>): Promise<void> | void {
    if (page.meta.page === 1 && page.meta.initialLazyLoadMessages) {
      return this.populateStore(chatId, page);
    } else {
      return this.unshiftMessagesByChatId(chatId, page);
    }
  }
  public populateOrUnshiftMessagesByChatIds(pages: { chat: string; page: PageI<MessageI> }[]): Promise<void> | void {
    const pagesToPopulateStore = pages.filter((row) => {
      const page = row?.page;
      const meta = page?.meta;
      return meta?.page === 1 && meta?.initialLazyLoadMessages;
    });

    const pagesToUnshift = pages.filter((page) => {
      return !pagesToPopulateStore.some((row) => row.chat === page.chat);
    });

    if (pagesToPopulateStore?.length) {
      return this.populateStorePages(pagesToPopulateStore);
    }

    if (pagesToUnshift?.length) {
      return this.unshiftMessagesByChatIds(pagesToUnshift);
    }
  }

  public addMessage(chatId: string, message: MessageI) {
    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [chatId]: {
          data: state.messages[chatId]?.data
            ? [...state.messages[chatId].data?.filter((m) => m._id !== message._id && m._id !== message.oldId), message]
            : [message],
          meta: state.messages[chatId]?.meta || null,
        },
      },
    }));

    if (!this.messageAddedSubjects[chatId]) {
      this.messageAddedSubjects[chatId] = new BehaviorSubject<MessageI | undefined>(undefined);
    }
    this.messageAddedSubjects[chatId].next(message);
  }

  private filterMessagesWrongTemplate(messages: MessageI[]): MessageI[] {
    const customerId = messages[0]?.customerId;
    if (!customerId) {
      return messages;
    }

    return messages.filter((message) => {
      if (message.contentWhatsappOficial?.template) {
        const imageComponent = message.contentWhatsappOficial.template.components?.find(
          (component) => component.type === 'header' && component.parameters?.some((param) => param.type === 'image')
        );

        if (imageComponent) {
          const imageLink = imageComponent.parameters?.find((param) => param.type === 'image')?.image?.link;

          if (imageLink && !imageLink.includes(customerId)) {
            return false;
          }
        }
      }
      return true;
    });
  }

  public scheduleMessage(chatId: string, message: MessageI) {
    const updateScheduledData = (existingData: MessageI[] | undefined) =>
      existingData
        ? [...existingData.filter((m) => m._id !== message._id && m._id !== message.oldId), message]
        : [message];

    this.setState((state) => ({
      ...state,
      scheduled: {
        ...state.scheduled,
        [chatId]: {
          data: updateScheduledData(state.scheduled[chatId]?.data),
          meta: state.scheduled[chatId]?.meta || null,
        },
        ['all']: {
          data: updateScheduledData(state.scheduled['all']?.data),
          meta: state.scheduled['all']?.meta || null,
        },
      },
    }));
  }

  public removeMessage(chatId: string, messageId: string) {
    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [chatId]: {
          data: state.messages[chatId]?.data?.filter((message) => message._id !== messageId),
          meta: state.messages[chatId]?.meta || null,
        },
      },
    }));
  }

  public removeScheduled(messageId: string) {
    this.setState((state) => ({
      ...state,
      scheduled: Object.fromEntries(
        Object.entries(state.scheduled).map(([chatId, paginator]) => [
          chatId,
          {
            ...paginator,
            data: paginator.data?.filter((message) => message._id !== messageId),
          },
        ])
      ),
    }));
  }

  public replaceScheduled(messageId: string, newMessage: MessageI) {
    this.setState((state) => ({
      ...state,
      scheduled: Object.fromEntries(
        Object.entries(state.scheduled).map(([chatId, paginator]) => [
          chatId,
          {
            ...paginator,
            data: paginator.data?.map((message) => (message._id === messageId ? newMessage : message)),
          },
        ])
      ),
    }));
  }

  public updateMessageById(chatId: string, messageId: string, updatedMessage: MessageI) {
    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [chatId]: {
          data: state.messages[chatId]?.data?.map((message) =>
            message._id === messageId || message._id === updatedMessage.oldId ? updatedMessage : message
          ),
          meta: state.messages[chatId]?.meta || null,
        },
      },
    }));
  }

  public getMessageAddedSubject(chatId: string): Observable<MessageI | undefined> {
    if (!this.messageAddedSubjects[chatId]) {
      this.messageAddedSubjects[chatId] = new BehaviorSubject<MessageI | undefined>(undefined);
    }
    return this.messageAddedSubjects[chatId].asObservable();
  }

  public updateAllMessageStatusByChat(
    chatId: string,
    status: MessageStatusT,
    updatingAgentMessages: boolean = false,
    type?: SenderTypeE
  ) {
    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [chatId]: {
          data: state.messages[chatId]?.data?.map((message) => {
            const typeMessage = this.messageHelperService.getSenderTypeByMessage(message);
            if (type && typeMessage !== type) {
              return message;
            }
            if (status === 'read') {
              if (typeMessage === SenderTypeE.CONTACT) {
                message.status = 'read';
              }
              if (updatingAgentMessages) {
                message.status = status;
              }
            } else {
              message.status = status;
            }
            return message;
          }),
          meta: state.messages[chatId]?.meta || null,
        },
      },
    }));
  }

  public updateLastMessageStatusByChat(chatId: string, status: MessageStatusT, type?: SenderTypeE) {
    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [chatId]: {
          data: state.messages[chatId]?.data?.map((message, index) => {
            const typeMessage = this.messageHelperService.getSenderTypeByMessage(message);
            if (type && typeMessage !== type) {
              return message;
            }
            if (state.messages[chatId]?.data?.length - 1 === index) {
              message.status = status;
            }
            return message;
          }),
          meta: state.messages[chatId]?.meta || null,
        },
      },
    }));
  }

  public updateMessageStatusById(chatId: string, messageId: string, status: MessageStatusT) {
    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [chatId]: {
          data: state.messages[chatId]?.data?.map((message) => {
            if (message._id === messageId) {
              message.status = status;
            }
            return message;
          }),
          meta: state.messages[chatId]?.meta || null,
        },
      },
    }));
  }
  public updateMessageFailStatusById(
    chatId: string,
    messageId: string,
    dataFail: DataFailMessageI,
    errors?: MessageErrorI[]
  ) {
    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [chatId]: {
          data: state.messages[chatId]?.data?.map((message) => {
            if (message._id === messageId) {
              return {
                ...message,
                dataFail,
                status: 'fail',
                errors,
              };
            }
            return message;
          }),
          meta: state.messages[chatId]?.meta || null,
        },
      },
    }));
  }

  public updateMessageDeleteStatusById(chatId: string, messageId: string) {
    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [chatId]: {
          data: state.messages[chatId]?.data?.map((message) => {
            if (message._id === messageId) {
              message.deletedMessage = true;
            }
            return message;
          }),
          meta: state.messages[chatId]?.meta || null,
        },
      },
    }));
  }

  public updateMessageTranscriptionById(chatId: string, messageId: string, transcription: TranscriptionI) {
    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [chatId]: {
          data: state.messages[chatId]?.data?.map((message) => {
            if (message._id === messageId) {
              message.transcription = transcription;
            }
            return message;
          }),
          meta: state.messages[chatId]?.meta || null,
        },
      },
    }));
  }

  public updateMessageId(chatId: string, tmpMessageId: string, messageId: string) {
    this.setState((state) => {
      const messages =
        state.messages[chatId]?.data?.map((msg) => {
          if (msg._id === tmpMessageId) {
            return { ...msg, _id: messageId };
          }
          return msg;
        }) || [];

      return {
        ...state,
        messages: {
          ...state.messages,
          [chatId]: {
            data: messages,
            meta: state.messages[chatId]?.meta || null,
          },
        },
      };
    });
  }

  public async existsMessageInStore(chatId: string, messageId: string): Promise<boolean> {
    const existsMessage = !!(await firstValueFrom(
      this.select((state) => {
        return state.messages[chatId]?.data?.find((message) => message._id === messageId);
      })
    ));

    return existsMessage;
  }
}
